'use client'

import React, { useState, useEffect } from 'react'

/**
 * ImpersonationBanner — shown on non-admin pages when Fabian is impersonating another user.
 *
 * Fetches impersonation state from /api/acl/impersonation on mount.
 * Renders a prominent purple banner with the impersonated email and a Stop button.
 * The banner is fixed at the top so it's always visible.
 */
export default function ImpersonationBanner() {
  const [active, setActive] = useState(false)
  const [targetEmail, setTargetEmail] = useState<string | null>(null)
  const [stopping, setStopping] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function check() {
      try {
        const res = await fetch('/api/acl/impersonation')
        if (res.ok) {
          const d = await res.json()
          if (!cancelled) {
            setActive(d.active)
            setTargetEmail(d.targetEmail)
          }
        }
      } catch { /* ignore fetch errors */ }
    }
    check()
    return () => { cancelled = true }
  }, [])

  const stop = async () => {
    setStopping(true)
    try {
      const res = await fetch('/api/acl/impersonation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'stop' }),
      })
      if (res.ok) {
        setActive(false)
        setTargetEmail(null)
        // Reload to reflect real permissions
        window.location.reload()
      }
    } catch { /* ignore */ }
    finally { setStopping(false) }
  }

  if (!active) return null

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 99999,
      background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
      color: '#fff',
      padding: '8px 16px',
      fontSize: '0.85rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    }}>
      <span>
        <strong>Impersonation Active</strong> — Viewing as <strong>{targetEmail}</strong>
      </span>
      <button
        onClick={stop}
        disabled={stopping}
        style={{
          background: '#fff',
          color: '#7c3aed',
          border: 'none',
          borderRadius: '6px',
          padding: '4px 14px',
          fontWeight: 700,
          cursor: 'pointer',
          fontSize: '0.8rem',
        }}
      >
        {stopping ? 'Stopping...' : 'Stop Impersonation'}
      </button>
      <a
        href="/admin/access-control"
        style={{
          color: '#fff',
          textDecoration: 'underline',
          fontSize: '0.8rem',
        }}
      >
        Go to Admin
      </a>
    </div>
  )
}
